import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { SearchInput } from "@/components/flexkit/SearchInput";
import { Checkbox } from "@/components/flexkit/checkbox/Checkbox";
import { Pill } from "@/components/podkit/status/Pill";
import type { SelectableSubject, Subject } from "@/components/settings/organization/groups/subject-types";
import { ServiceAccountAvatar } from "@/components/settings/organization/service-accounts/ServiceAccountAvatar";
import { useListMembersInfinite } from "@/queries/organization-queries";
import { useListServiceAccounts } from "@/queries/service-account-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { Principal } from "gitpod-next-api/gitpod/v1/identity_pb";
import { useCallback, useMemo, useState, type FC } from "react";
import { useDebounce } from "use-debounce";
export type { SelectableSubject, Subject } from "@/components/settings/organization/groups/subject-types";

type MemberSelectionListProps = {
    /** Group IDs whose members should be excluded from the list (server-side filtering) */
    excludeGroupIds?: string[];
    /** Subject IDs to exclude client-side (e.g. service accounts already in the group) */
    excludeSubjectIds?: Set<string>;
    selectedSubjects?: Map<string, Subject>;
    onChangeSelection: (selectedSubjects: Map<string, Subject>) => void;
    showSearch?: boolean;
    showSelectedCount?: boolean;
    className?: string;
};

export const MemberSelectionList: FC<MemberSelectionListProps> = ({
    excludeGroupIds,
    excludeSubjectIds,
    selectedSubjects,
    onChangeSelection,
    showSearch = true,
    showSelectedCount = true,
    className,
}) => {
    const effectiveExcludeIds = excludeSubjectIds;
    const effectiveSelectedSubjects = useMemo(
        () => selectedSubjects ?? new Map<string, Subject>(),
        [selectedSubjects],
    );

    const { data: user } = useAuthenticatedUser();
    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearch] = useDebounce(searchValue.trim(), 250, { trailing: true });

    const { data: membersPages, hasNextPage, fetchNextPage, isFetchingNextPage, isFetching } = useListMembersInfinite({
        search: debouncedSearch || undefined,
        excludeGroupIds,
    });
    const { data: serviceAccountsData } = useListServiceAccounts();

    // Flatten paginated members into a single list
    const allMembers = useMemo(() => {
        if (!membersPages?.pages) return [];
        return membersPages.pages.flatMap((page) => page.members);
    }, [membersPages?.pages]);

    // Combine users and service accounts into a unified list
    const availableSubjects = useMemo(() => {
        const subjects: SelectableSubject[] = [];

        for (const member of allMembers) {
            if (member.userId && !effectiveExcludeIds?.has(member.userId)) {
                const loginProvider = (() => {
                    switch (member.loginProvider) {
                        case "magiclink":
                            return "Email";
                        case "github":
                            return "GitHub";
                        case "gitlab":
                            return "GitLab";
                        case "google":
                            return "Google";
                        case "custom":
                            return "SSO";
                        default:
                            return "";
                    }
                })();
                subjects.push({
                    id: member.userId,
                    principal: Principal.USER,
                    name: member.fullName || "",
                    avatarUrl: member.avatarUrl,
                    secondaryText: loginProvider,
                    email: member.email,
                    isCurrentUser: member.userId === user?.id,
                });
            }
        }

        // Add service accounts (not filtered by exclude_group_ids since that's user-only)
        const query = debouncedSearch.toLowerCase();
        if (serviceAccountsData?.serviceAccounts) {
            for (const sa of serviceAccountsData.serviceAccounts) {
                if (sa.id && !effectiveExcludeIds?.has(sa.id)) {
                    // Filter service accounts client-side by search since they aren't in listMembers
                    if (query && !sa.name?.toLowerCase().includes(query)) {
                        continue;
                    }
                    subjects.push({
                        id: sa.id,
                        principal: Principal.SERVICE_ACCOUNT,
                        name: sa.name || "",
                        secondaryText: "Service Account",
                    });
                }
            }
        }

        return subjects.sort((a, b) => a.name.localeCompare(b.name));
    }, [allMembers, serviceAccountsData?.serviceAccounts, effectiveExcludeIds, user?.id, debouncedSearch]);

    const selectedCount = effectiveSelectedSubjects.size;

    const handleSelectionChange = useCallback(
        (subject: SelectableSubject, checked: boolean) => {
            const newMap = new Map(effectiveSelectedSubjects);
            if (checked) {
                newMap.set(subject.id, { id: subject.id, principal: subject.principal });
            } else {
                newMap.delete(subject.id);
            }
            onChangeSelection(newMap);
        },
        [effectiveSelectedSubjects, onChangeSelection],
    );

    return (
        <div className={className}>
            {/* Search Input */}
            {showSearch && (
                <SearchInput
                    wrapperClassName="w-full"
                    className="max-w-none"
                    placeholder="Search by name or keyword"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onClear={() => setSearchValue("")}
                    isLoading={isFetching || isFetchingNextPage}
                    data-tracking-id="member-selection-search"
                />
            )}

            {/* Subjects List */}
            <ScrollArea
                orientation="vertical"
                className="min-h-0 flex-1 rounded-lg border border-border-base"
                onScrollEnd={() => {
                    if (hasNextPage && !isFetchingNextPage) {
                        void fetchNextPage();
                    }
                }}
                scrollEndThreshold={500}
            >
                {availableSubjects.length === 0 ? (
                    <div className="flex h-full items-center justify-center px-4 py-8 text-center text-sm text-content-secondary">
                        {searchValue.trim()
                            ? "No members found matching your search."
                            : "No available members to add."}
                    </div>
                ) : (
                    <div className="divide-y divide-border-base">
                        {availableSubjects.map((subject) => {
                            const isSelected = effectiveSelectedSubjects.has(subject.id);
                            const isServiceAccount = subject.principal === Principal.SERVICE_ACCOUNT;
                            return (
                                <label
                                    key={subject.id}
                                    htmlFor={`member-checkbox-${subject.id}`}
                                    className="ml-2 flex cursor-pointer items-center gap-2 px-2 py-2 hover:bg-surface-secondary"
                                    data-tracking-id="member-selection-row"
                                >
                                    <Checkbox
                                        id={`member-checkbox-${subject.id}`}
                                        checked={isSelected}
                                        onCheckedChange={(checked) => handleSelectionChange(subject, !!checked)}
                                        data-testid={`member-checkbox-${subject.id}`}
                                        data-tracking-id="member-selection-checkbox"
                                    />
                                    {isServiceAccount ? (
                                        <ServiceAccountAvatar
                                            id={subject.id}
                                            size={24}
                                            className="size-6 flex-shrink-0"
                                        />
                                    ) : (
                                        <Avatar
                                            size={24}
                                            className="size-6 flex-shrink-0 rounded-full border-2 border-surface-glass bg-surface-glass ring-1 ring-border-light"
                                        >
                                            {subject.avatarUrl && (
                                                <AvatarImage src={subject.avatarUrl} alt={`${subject.name}'s avatar`} />
                                            )}
                                            <AvatarFallback>
                                                <AvatarInitials name={subject.name} size={24} />
                                            </AvatarFallback>
                                        </Avatar>
                                    )}
                                    <div className="flex min-w-0 flex-1 items-center gap-2">
                                        <p className="truncate font-medium text-content-primary">{subject.name}</p>
                                        {subject.isCurrentUser && <Pill variant="success">me</Pill>}
                                        {subject.secondaryText && (
                                            <span className="text-sm text-content-secondary">
                                                {subject.secondaryText}
                                            </span>
                                        )}
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                )}
            </ScrollArea>

            {showSelectedCount && (
                <p className="flex-shrink-0 text-sm text-content-secondary" style={{ minHeight: "1.25rem" }}>
                    {selectedCount > 0 && `${selectedCount} ${selectedCount === 1 ? "member" : "members"} selected`}
                </p>
            )}
        </div>
    );
};
